import { TankData, BulletData, ObstacleData } from "./stores/useRocketanks";
import { apiFetch } from "./apiBase";

export interface GameStateForAI {
  myTank: {
    id: string;
    x: number;
    z: number;
    rotation: number;
    health: number;
    maxHealth: number;
  };
  enemyTanks: Array<{
    id: string;
    x: number;
    z: number;
    health: number;
    isAlive: boolean;
  }>;
  bullets: Array<{
    x: number;
    z: number;
    directionX: number;
    directionZ: number;
  }>;
  obstacles: Array<{
    x: number;
    z: number;
    width: number;
    depth: number;
    isDestructible: boolean;
  }>;
  arenaSize: number;
}

export interface AIDecision {
  moveX: number;
  moveZ: number;
  aimX: number;
  aimZ: number;
  shouldFire: boolean;
  reasoning?: string;
  thought?: string;
  isOriginalThought?: boolean;
  providerUsed?: "openai" | "gemini";   // Qual provider processou a request
  providerSwitched?: boolean;            // Se houve troca automática (rate limit)
}

// Intervalo mínimo entre requests (ms)
// O servidor tem cache agressivo, então podemos ser mais generosos aqui
const REQUEST_INTERVAL = 800; // Aumentado de 500ms para 800ms para reduzir carga

interface AIState {
  pendingRequest: Promise<AIDecision> | null;
  lastDecision: AIDecision;
  lastRequestTime: number;
}

const aiStates: Map<string, AIState> = new Map();

function getOrCreateAIState(aiId: string): AIState {
  if (!aiStates.has(aiId)) {
    aiStates.set(aiId, {
      pendingRequest: null,
      lastDecision: {
        moveX: 0,
        moveZ: 0,
        aimX: 0,
        aimZ: -1,
        shouldFire: false,
        thought: "Awakening...",
      },
      lastRequestTime: 0,
    });
  }
  return aiStates.get(aiId)!;
}

export function buildGameStateForAI(
  myTank: TankData,
  enemyTanks: TankData[],
  playerTank: TankData | null,
  bullets: BulletData[],
  obstacles: ObstacleData[],
  arenaSize: number
): GameStateForAI {
  const allEnemies = playerTank && playerTank.id !== myTank.id
    ? [playerTank, ...enemyTanks.filter(e => e.id !== myTank.id)]
    : enemyTanks.filter(e => e.id !== myTank.id);

  return {
    myTank: {
      id: myTank.id,
      x: myTank.position.x,
      z: myTank.position.z,
      rotation: myTank.rotation,
      health: myTank.health,
      maxHealth: myTank.maxHealth,
    },
    enemyTanks: allEnemies.map(e => ({
      id: e.id,
      x: e.position.x,
      z: e.position.z,
      health: e.health,
      isAlive: e.isAlive,
    })),
    bullets: bullets
      .filter(b => b.ownerId !== myTank.id)
      .slice(0, 10)
      .map(b => ({
        x: b.position.x,
        z: b.position.z,
        directionX: b.direction.x,
        directionZ: b.direction.z,
      })),
    obstacles: obstacles
      .filter(o => o.health > 0)
      .filter(o => {
        const dx = Math.abs(o.position.x - myTank.position.x);
        const dz = Math.abs(o.position.z - myTank.position.z);
        return dx < 25 && dz < 25;
      })
      .slice(0, 20)
      .map(o => ({
        x: o.position.x,
        z: o.position.z,
        width: o.size.x,
        depth: o.size.z,
        isDestructible: o.type === "destructible",
      })),
    arenaSize,
  };
}

export async function getAIDecision(
  gameState: GameStateForAI,
  personality: "aggressive" | "defensive" | "strategic",
  provider: "openai" | "gemini" = "openai"
): Promise<AIDecision> {
  const aiId = gameState.myTank.id;
  const state = getOrCreateAIState(aiId);
  const now = Date.now();

  if (now - state.lastRequestTime < REQUEST_INTERVAL) {
    return state.lastDecision;
  }

  if (state.pendingRequest) {
    return state.lastDecision;
  }

  state.lastRequestTime = now;

  state.pendingRequest = apiFetch("/api/genai/decision", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ gameState, personality, provider }),
  })
    .then(res => {
      if (!res.ok) throw new Error("API error");
      return res.json();
    })
    .then((decision: AIDecision) => {
      state.lastDecision = decision;
      return decision;
    })
    .catch(err => {
      console.error("[GenAI Client] Error for", aiId, ":", err);
      return state.lastDecision;
    })
    .finally(() => {
      state.pendingRequest = null;
    });
  
  return state.pendingRequest;
}

export function getLastDecision(aiId?: string): AIDecision {
  if (aiId) {
    return getOrCreateAIState(aiId).lastDecision;
  }
  const firstState = aiStates.values().next().value;
  return firstState?.lastDecision || {
    moveX: 0,
    moveZ: 0,
    aimX: 0,
    aimZ: -1,
    shouldFire: false,
    thought: "...",
  };
}

export function resetAIState(aiId?: string) {
  if (aiId) {
    aiStates.delete(aiId);
  } else {
    aiStates.clear();
  }
}

export function getLastThought(aiId?: string): string {
  if (aiId) {
    return getOrCreateAIState(aiId).lastDecision.thought || "...";
  }
  const firstState = aiStates.values().next().value;
  return firstState?.lastDecision.thought || "...";
}

export async function checkProviderStatus(): Promise<{ openai: boolean; gemini: boolean }> {
  try {
    const res = await apiFetch("/api/genai/status");
    if (!res.ok) return { openai: false, gemini: false };
    return await res.json();
  } catch {
    return { openai: false, gemini: false };
  }
}
